/**
 * DRPX Courier — Firma-Adressbuch und Fuhrpark (Supabase)
 *
 * CRUD für die `firma_adressbuch`-Tabelle (wiederkehrende Abhol-/Ziel-
 * adressen für schnelleres Auftragsanlegen) und für `firma_fahrzeuge`
 * (Fuhrpark einer Spedition, Migration 126).
 *
 * @module lib/supabase/firma
 */

import { _sb } from './client.js';
import { mapFirmaAdresseFromDb } from './mappers.js';
import { camelToSnake } from '../mapper.js';
import type { FirmaFahrzeug } from '../../types.js';

// ════════════════════════════════════════════════════════════════════
// Interne Helper
// ════════════════════════════════════════════════════════════════════

const NO_CLIENT_ERROR = new Error('Supabase client not initialized');

// ════════════════════════════════════════════════════════════════════
// Lokale Interfaces (nicht exportiert)
// ════════════════════════════════════════════════════════════════════

/**
 * Adressbuch-Eintrag-Shape. Es gibt noch keinen `FirmaAdresse`-Typ in
 * `types.ts` — lokales Interface, wandert dorthin bei späterer
 * Erweiterung.
 */
interface FirmaAdresseInput {
  name?: string;
  adresse?: string;
  plz?: string;
  ort?: string;
  land?: string;
  firma?: string;
  ansprech?: string;
  telefon?: string;
  email?: string;
  notizen?: string;
}

// ════════════════════════════════════════════════════════════════════
// Update-Whitelist
// ════════════════════════════════════════════════════════════════════

/**
 * Whitelist der Felder, die per `updateFirmaAdresse` geändert werden
 * dürfen. Verhindert versehentliches Überschreiben von `id`, `firma_id`,
 * `created_at` etc.
 *
 * Hinweis: Alle Feldnamen sind Einzelworte — `camelToSnake` ist hier ein
 * No-Op. Die Whitelist-Konsistenz mit `profiles.ts` und `auftraege.ts`
 * ist trotzdem gewollt (Compile-Time-Validierung + Sicherheits-Schutz).
 */
const ADRESSE_UPDATE_WHITELIST = [
  'name',
  'adresse',
  'plz',
  'ort',
  'land',
  'firma',
  'ansprech',
  'telefon',
  'email',
  'notizen',
] as const satisfies readonly (keyof FirmaAdresseInput)[];

// ════════════════════════════════════════════════════════════════════
// FIRMA ADRESSBUCH
// ════════════════════════════════════════════════════════════════════

/**
 * Lädt das komplette Adressbuch einer Firma (wiederkehrende Abhol-/
 * Zieladressen für schnelleres Auftragsanlegen).
 */
export async function getFirmaAdressen(firmaId: string): Promise<{
  data: Array<Record<string, unknown>>;
  error?: unknown;
}> {
  if (!_sb) return { data: [], error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client
      .from('firma_adressbuch')
      .select('*')
      .eq('firma_id', firmaId)
      .order('created_at', { ascending: false });
    const rows = (r.data || []) as Array<Record<string, unknown>>;
    const mapped = rows
      .map((row) => mapFirmaAdresseFromDb(row))
      .filter((row): row is Record<string, unknown> => row !== null);
    return { data: mapped };
  } catch (e: unknown) {
    return { data: [], error: e };
  }
}

/**
 * Legt einen neuen Adressbuch-Eintrag für eine Firma an.
 */
export async function createFirmaAdresse(
  firmaId: string,
  a: FirmaAdresseInput,
): Promise<{ data: Record<string, unknown> | null; error: unknown }> {
  if (!_sb) return { data: null, error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const d = {
      firma_id: firmaId,
      name: a.name || '',
      adresse: a.adresse || '',
      plz: a.plz || '',
      ort: a.ort || '',
      land: a.land || 'DE',
      firma: a.firma || '',
      ansprech: a.ansprech || '',
      telefon: a.telefon || '',
      email: a.email || '',
      notizen: a.notizen || '',
    };
    const r = await client.from('firma_adressbuch').insert(d).select().single();
    return {
      data: r.data ? mapFirmaAdresseFromDb(r.data as Record<string, unknown>) : null,
      error: r.error,
    };
  } catch (e: unknown) {
    return { data: null, error: e };
  }
}

/**
 * Aktualisiert einen Adressbuch-Eintrag. Nur übergebene Felder werden
 * geändert.
 */
export async function updateFirmaAdresse(
  id: string,
  updates: FirmaAdresseInput,
): Promise<{ data: Record<string, unknown> | null; error: unknown }> {
  if (!_sb) return { data: null, error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const d: Record<string, unknown> = {};
    for (const key of ADRESSE_UPDATE_WHITELIST) {
      if (updates[key] !== undefined) {
        d[camelToSnake(key)] = updates[key];
      }
    }
    const r = await client
      .from('firma_adressbuch')
      .update(d)
      .eq('id', id)
      .select()
      .single();
    return {
      data: r.data ? mapFirmaAdresseFromDb(r.data as Record<string, unknown>) : null,
      error: r.error,
    };
  } catch (e: unknown) {
    return { data: null, error: e };
  }
}

/**
 * Löscht einen Adressbuch-Eintrag unwiderruflich.
 */
export async function deleteFirmaAdresse(id: string): Promise<{ error: unknown }> {
  if (!_sb) return { error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client.from('firma_adressbuch').delete().eq('id', id);
    return { error: r.error };
  } catch (e: unknown) {
    return { error: e };
  }
}

// ════════════════════════════════════════════════════════════════════
// FUHRPARK (Migration 126)
// ════════════════════════════════════════════════════════════════════

/** Spalten, die beim Laden und nach dem Speichern gelesen werden. */
const FAHRZEUG_SPALTEN = 'id, bezeichnung, fahrzeug_typ, kennzeichen, ausstattung';

/**
 * Eingabe zum Anlegen oder Ändern eines Fahrzeugs. Prüfen
 * (Pflichtfelder, doppeltes Kennzeichen, Höchstzahl) muss der Aufrufer
 * vorher mit `pruefeFahrzeug` aus `lib/fuhrpark.ts`; die Datenbank prüft
 * zusätzlich Längen und Eindeutigkeit.
 */
export type FirmaFahrzeugInput = Omit<FirmaFahrzeug, 'id'>;

/**
 * Liest eine Zeile aus `firma_fahrzeuge` defensiv ein.
 *
 * @returns null, wenn Pflichtfelder fehlen (dann wird die Zeile übersprungen)
 */
function mapFahrzeugFromDb(row: Record<string, unknown>): FirmaFahrzeug | null {
  if (typeof row.id !== 'string') return null;
  if (typeof row.fahrzeug_typ !== 'string' || typeof row.kennzeichen !== 'string') return null;
  const ausstattung: Record<string, true> = {};
  const a = row.ausstattung;
  if (a && typeof a === 'object' && !Array.isArray(a)) {
    for (const [k, v] of Object.entries(a as Record<string, unknown>)) {
      if (v === true) ausstattung[k] = true;
    }
  }
  return {
    id: row.id,
    bezeichnung: typeof row.bezeichnung === 'string' ? row.bezeichnung : '',
    fahrzeugTyp: row.fahrzeug_typ,
    kennzeichen: row.kennzeichen,
    ausstattung,
  };
}

/** Wandelt die Eingabe in eine Datenbank-Zeile (ohne id/firma_id). */
function fahrzeugZuDb(f: FirmaFahrzeugInput): Record<string, unknown> {
  return {
    bezeichnung: f.bezeichnung.trim().slice(0, 60),
    fahrzeug_typ: f.fahrzeugTyp.trim().slice(0, 60),
    kennzeichen: f.kennzeichen.trim().slice(0, 20),
    ausstattung: f.ausstattung || {},
  };
}

/**
 * Lädt den eigenen Fuhrpark, sortiert nach Anlage (ältestes zuerst).
 * Die RLS gibt nur eigene Fahrzeuge frei — kein Filter auf firma_id nötig.
 */
export async function getFirmaFahrzeuge(): Promise<{ data: FirmaFahrzeug[]; error?: unknown }> {
  if (!_sb) return { data: [], error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client
      .from('firma_fahrzeuge')
      .select(FAHRZEUG_SPALTEN)
      .order('created_at', { ascending: true });
    if (r.error) return { data: [], error: r.error };
    const rows = (r.data || []) as Array<Record<string, unknown>>;
    return { data: rows.map(mapFahrzeugFromDb).filter((f): f is FirmaFahrzeug => f !== null) };
  } catch (e: unknown) {
    return { data: [], error: e };
  }
}

/**
 * Legt ein Fahrzeug an. `firma_id` setzt die Datenbank (DEFAULT meine_firma_id(), Migration 137 — beim Mitarbeiter die Firma).
 * Fehlercode 23505 = Kennzeichen gibt es in dieser Firma schon.
 */
export async function createFirmaFahrzeug(
  f: FirmaFahrzeugInput,
): Promise<{ data: FirmaFahrzeug | null; error: unknown }> {
  if (!_sb) return { data: null, error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client
      .from('firma_fahrzeuge')
      .insert(fahrzeugZuDb(f))
      .select(FAHRZEUG_SPALTEN)
      .single();
    return {
      data: r.data ? mapFahrzeugFromDb(r.data as Record<string, unknown>) : null,
      error: r.error,
    };
  } catch (e: unknown) {
    return { data: null, error: e };
  }
}

/** Ändert ein eigenes Fahrzeug (alle Felder außer id). */
export async function updateFirmaFahrzeug(
  id: string,
  f: FirmaFahrzeugInput,
): Promise<{ data: FirmaFahrzeug | null; error: unknown }> {
  if (!_sb) return { data: null, error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client
      .from('firma_fahrzeuge')
      .update(fahrzeugZuDb(f))
      .eq('id', id)
      .select(FAHRZEUG_SPALTEN)
      .single();
    return {
      data: r.data ? mapFahrzeugFromDb(r.data as Record<string, unknown>) : null,
      error: r.error,
    };
  } catch (e: unknown) {
    return { data: null, error: e };
  }
}

/** Löscht ein eigenes Fahrzeug. Alte Angebote behalten ihr Kennzeichen (Text). */
export async function deleteFirmaFahrzeug(id: string): Promise<{ error: unknown }> {
  if (!_sb) return { error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client.from('firma_fahrzeuge').delete().eq('id', id);
    return { error: r.error };
  } catch (e: unknown) {
    return { error: e };
  }
}
